import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { PageHeader, Badge, EmptyState } from "@/components/common";
import { ScrollText, Filter } from "lucide-react";

const AKSI = {
  create: { label: "Tambah", cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" },
  bulk_create: { label: "Impor Massal", cls: "bg-cyan-500/10 text-cyan-300 border-cyan-500/30" },
  update: { label: "Ubah", cls: "bg-amber-500/10 text-amber-300 border-amber-500/30" },
  delete: { label: "Hapus", cls: "bg-rose-500/10 text-rose-300 border-rose-500/30" },
  login: { label: "Login", cls: "bg-indigo-500/10 text-indigo-300 border-indigo-500/30" },
};

const ENTITAS = {
  inventory: "Stok Produk",
  event: "Event",
  sale: "Penjualan",
  preorder: "Pre-Order",
  user: "Pengguna",
  auth: "Autentikasi",
  adjustment: "Penyesuaian Stok",
  trade: "Trade",
  return: "Refund/Retur",
};

const inputCls =
  "px-3 py-2 rounded-lg bg-slate-950/60 border border-slate-700 text-white text-sm focus:outline-none focus:border-indigo-500 transition";

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [entitas, setEntitas] = useState("all");
  const [aksi, setAksi] = useState("all");

  const load = useCallback(async () => {
    const params = {};
    if (entitas !== "all") params.entitas = entitas;
    if (aksi !== "all") params.aksi = aksi;
    const { data } = await api.get("/audit", { params });
    setLogs(data);
  }, [entitas, aksi]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <PageHeader title="Audit Log" subtitle="Jejak Aktivitas" />

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex items-center gap-2 text-slate-500">
          <Filter className="w-4 h-4" />
          <span className="text-xs font-mono uppercase tracking-wider">Filter</span>
        </div>
        <select value={entitas} onChange={(e) => setEntitas(e.target.value)} className={inputCls} data-testid="audit-filter-entitas">
          <option value="all">Semua Modul</option>
          {Object.entries(ENTITAS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select value={aksi} onChange={(e) => setAksi(e.target.value)} className={inputCls} data-testid="audit-filter-aksi">
          <option value="all">Semua Aksi</option>
          {Object.entries(AKSI).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <span className="text-xs text-slate-500 font-mono ml-auto">{logs.length} entri</span>
      </div>

      {logs.length === 0 ? (
        <EmptyState icon={ScrollText} title="Belum ada aktivitas" subtitle="Setiap perubahan data (tambah, ubah, hapus, login) akan tercatat di sini." />
      ) : (
        <div className="rounded-xl border border-slate-800 bg-[#131822] overflow-hidden" data-testid="audit-list">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs font-mono uppercase tracking-wider text-slate-500 border-b border-slate-800">
                  <th className="text-left px-5 py-3">Waktu</th>
                  <th className="text-left px-5 py-3">Pengguna</th>
                  <th className="text-left px-5 py-3">Aksi</th>
                  <th className="text-left px-5 py-3">Modul</th>
                  <th className="text-left px-5 py-3">Detail</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => {
                  const a = AKSI[l.aksi] || { label: l.aksi, cls: "bg-slate-500/10 text-slate-300 border-slate-500/30" };
                  return (
                    <tr key={l.id} className="border-b border-slate-800/60 hover:bg-slate-800/30 transition">
                      <td className="px-5 py-3 text-slate-400 font-mono text-xs whitespace-nowrap">
                        {new Date(l.created_at).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" })}
                      </td>
                      <td className="px-5 py-3">
                        <p className="text-white font-medium">{l.user_name || "-"}</p>
                        <p className="text-[11px] text-slate-500">{l.user_role}</p>
                      </td>
                      <td className="px-5 py-3"><Badge className={a.cls}>{a.label}</Badge></td>
                      <td className="px-5 py-3 text-slate-400">{ENTITAS[l.entitas] || l.entitas}</td>
                      <td className="px-5 py-3 text-slate-300">{l.detail}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
